/*
 * "Recently used" and the skin tone, as the picker and the ":" source read them: one store for the
 * whole tab rather than state in the picker. The picker is mounted and unmounted once per message
 * row it is opened from, and a list that is refetched on every open would blink. The list is also
 * updated optimistically — the emoji a person just picked belongs at the front before the round
 * trip, and the next answer is what corrects it.
 *
 * Who *keeps* them is not core's business. Chat records them on the server so that they follow a
 * person from device to device (`chat_emoji_use` and the setting `chat.emoji.skin_tone`), and it
 * hands core the three calls behind that when it binds (`provideEmojiHistory`). A tab with no keeper
 * still has a whole picker: no "recently used" row, and a tone that holds until the page reloads.
 */

import { useEffect } from "react"
import { Shared, useShared } from "../store/shared.ts"
import { MAX_SKIN_TONE } from "./table.ts"

/** How many the picker's first row shows; a keeper may hand over more. */
const SHOWN = 24

export interface EmojiPrefs {
	/** Sequences, the most recently used first. */
	recents: readonly string[]
	/** 0 is the neutral form; 1 to 5 are the Fitzpatrick modifiers. */
	skinTone: number
}

/** The module that keeps a person's emoji history, as core calls it. */
export interface EmojiHistory {
	/** The history and the tone as the keeper has them. */
	load(): Promise<EmojiPrefs>
	/** These sequences were just used. Fire and forget: a failure costs a row's order, nothing more. */
	note(sequences: readonly string[]): void
	/** The tone was chosen. Quiet on failure: the tone holds for this tab either way. */
	saveTone(tone: number): void
}

const prefs = new Shared<EmojiPrefs>({ recents: [], skinTone: 0 })

let history: EmojiHistory | undefined
let fetching: Promise<void> | undefined

function toneOrNeutral(tone: number): number {
	return tone >= 0 && tone <= MAX_SKIN_TONE ? tone : 0
}

function publish(next: EmojiPrefs): void {
	prefs.set({ recents: next.recents.slice(0, SHOWN), skinTone: toneOrNeutral(next.skinTone) })
}

/**
 * Installs the keeper; answers the call that takes it away again. The history is read afresh from
 * the new keeper on the next read, because what an earlier one answered may be somebody else's.
 */
export function provideEmojiHistory(keeper: EmojiHistory): () => void {
	history = keeper
	fetching = undefined
	return () => {
		if (history === keeper) history = undefined
	}
}

/**
 * Loads the history once per tab. A refusal is not an error a person needs to see: the picker
 * without its recents row is still a picker, and the next open tries again.
 */
export function loadEmojiPrefs(): Promise<void> {
	const keeper = history
	if (keeper === undefined) return Promise.resolve()
	fetching ??= keeper
		.load()
		.then(publish)
		.catch(() => {
			fetching = undefined
		})
	return fetching
}

/**
 * The keeper's own list, pushed: another tab or another device used an emoji, and this one follows
 * without a refetch.
 */
export function applyEmojiPrefs(next: EmojiPrefs): void {
	publish(next)
}

/**
 * The current preferences, as React state. Mounting the first reader starts the fetch — the
 * picker draws its categories immediately and the "recently used" row appears when it answers, so
 * nothing waits on a request that may never come back.
 */
export function useEmojiPrefs(): EmojiPrefs {
	useEffect(() => {
		void loadEmojiPrefs()
	}, [])
	return useShared(prefs)
}

/** The tone an insertion uses now, for the code that inserts outside React (the ":" source). */
export function emojiSkinTone(): number {
	return prefs.now().skinTone
}

/** Puts one sequence at the front of the history, here and with the keeper. */
export function rememberEmoji(char: string): void {
	const held = prefs.now()
	publish({ ...held, recents: [char, ...held.recents.filter((entry) => entry !== char)] })
	history?.note([char])
}

/**
 * Sets the tone the picker and the ":" source insert with; 0 is the neutral form. It applies here
 * first and is handed to the keeper afterwards.
 */
export function setSkinTone(tone: number): void {
	const next = toneOrNeutral(tone)
	publish({ ...prefs.now(), skinTone: next })
	history?.saveTone(next)
}

/** Test support: forgets what this tab has loaded so the next read starts over. */
export function resetEmojiPrefs(): void {
	fetching = undefined
	publish({ recents: [], skinTone: 0 })
}
